import { FormField, Select, SelectProps, SpaceBetween } from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import { listDripCampaigns } from '../../common/api/dripCampaigns';
import { listZipBlasts } from '../../common/api/zipBlasts';

export type DripScope = {
  campaign_uuid: string | null;
  zip_blast_uuid: string | null;
};

/**
 * URL-backed campaign/zip-blast scope state shared by the drip dashboard,
 * contacts, and emails views (?campaign_uuid=...&zip_blast_uuid=...).
 */
export function useUrlDripScope() {
  const [campaignUuid, setCampaignUuid] = useQueryParam('campaign_uuid', StringParam);
  const [zipBlastUuid, setZipBlastUuid] = useQueryParam('zip_blast_uuid', StringParam);

  const scope: DripScope = useMemo(
    () => ({
      campaign_uuid: campaignUuid ?? null,
      zip_blast_uuid: zipBlastUuid ?? null,
    }),
    [campaignUuid, zipBlastUuid],
  );

  return {
    scope,
    setCampaignUuid: (uuid: string | null) => setCampaignUuid(uuid ?? undefined),
    setZipBlastUuid: (uuid: string | null) => setZipBlastUuid(uuid ?? undefined),
  };
}

const ALL_CAMPAIGNS: SelectProps.Option = { label: 'All campaigns', value: '' };
const ALL_ZIP_BLASTS: SelectProps.Option = { label: 'All zip blasts', value: '' };

export type DripScopeFilterProps = {
  scope: DripScope;
  setCampaignUuid: (uuid: string | null) => void;
  setZipBlastUuid: (uuid: string | null) => void;
  onChange?: () => void;
};

/**
 * Campaign + zip blast scope selectors. Selecting a zip blast narrows results
 * to emails/contacts attributed to that single blast; selecting a campaign
 * narrows to the campaign across all blasts.
 */
export function DripScopeFilter(props: DripScopeFilterProps) {
  const { scope, setCampaignUuid, setZipBlastUuid, onChange } = props;

  const campaignsQuery = useQuery({
    queryKey: ['drip-campaigns', 30],
    queryFn: () => listDripCampaigns(30),
    staleTime: 300_000,
  });
  const zipBlastsQuery = useQuery({
    queryKey: ['zip-blasts', 1, 100],
    queryFn: () => listZipBlasts(1, 100),
    staleTime: 300_000,
  });

  const campaignOptions: SelectProps.Options = useMemo(
    () => [
      ALL_CAMPAIGNS,
      ...(campaignsQuery.data ?? []).map((c) => ({ label: c.name, value: c.uuid })),
    ],
    [campaignsQuery.data],
  );
  const zipBlastOptions: SelectProps.Options = useMemo(
    () => [
      ALL_ZIP_BLASTS,
      ...(zipBlastsQuery.data?.items ?? []).map((b) => ({
        label: `${b.zip_code} · ${new Date(b.created_at).toLocaleDateString()}`,
        value: b.uuid,
        description: b.status,
      })),
    ],
    [zipBlastsQuery.data],
  );

  const selectedCampaign =
    campaignOptions.find(
      (o): o is SelectProps.Option => 'value' in o && o.value === (scope.campaign_uuid ?? ''),
    ) ?? ALL_CAMPAIGNS;
  const selectedZipBlast =
    zipBlastOptions.find(
      (o): o is SelectProps.Option => 'value' in o && o.value === (scope.zip_blast_uuid ?? ''),
    ) ?? ALL_ZIP_BLASTS;

  return (
    <SpaceBetween direction="horizontal" size="s">
      <FormField label="Campaign">
        <Select
          data-testid="drip-scope-campaign"
          selectedOption={selectedCampaign}
          options={campaignOptions}
          loadingText="Loading campaigns…"
          statusType={campaignsQuery.isLoading ? 'loading' : 'finished'}
          onChange={({ detail }) => {
            setCampaignUuid(detail.selectedOption.value || null);
            onChange?.();
          }}
        />
      </FormField>
      <FormField label="Zip blast">
        <Select
          data-testid="drip-scope-zip-blast"
          selectedOption={selectedZipBlast}
          options={zipBlastOptions}
          loadingText="Loading zip blasts…"
          statusType={zipBlastsQuery.isLoading ? 'loading' : 'finished'}
          onChange={({ detail }) => {
            setZipBlastUuid(detail.selectedOption.value || null);
            onChange?.();
          }}
        />
      </FormField>
    </SpaceBetween>
  );
}
